import { ReactNode } from 'react';
import { Container, Box, AppBar, Toolbar, styled, Button } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import logoImage from 'modules/app/image/logo.svg';

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  top: 0,
  zIndex: theme.zIndex.appBar,
}));

const ToolbarStyled = styled(Toolbar)({
  justifyContent: 'space-between',
});

const ContainerStyled = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  paddingTop: theme.spacing(5),
}));

const MainStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minWidth: 0,
  paddingBottom: theme.spacing(5),
}));

export interface LightLayoutProps {
  children: ReactNode;
  position?: 'fixed' | 'absolute' | 'relative' | 'static' | 'sticky';
  showBackButton?: boolean;
  onBackClick?: () => void;
}

export const LightLayout = ({
  children,
  position = 'relative',
  showBackButton = false,
  onBackClick,
}: LightLayoutProps) => {
  const { t } = useTranslation('common');
  const history = useHistory();

  const handleBack = () => {
    if (onBackClick) onBackClick();
    else history.push('/dashboard');
  };

  return (
    <Box display="flex" flexDirection="column" minHeight="100%">
      <AppBarStyled color="default" position={position}>
        <Container maxWidth="lg">
          <ToolbarStyled disableGutters>
            <Box display="flex" alignItems="center">
              <Box width={170} component="img" src={logoImage} alt="logo" />
            </Box>

            {showBackButton && (
              <Button
                onClick={handleBack}
                color="secondary"
                startIcon={<ArrowBackIosNewRoundedIcon />}
                sx={{
                  justifyContent: 'flex-start',
                  mr: { md: 4 },
                  mt: { xs: 1, md: 0 },
                  mb: { xs: 1, md: 0 },
                }}
              >
                 {t('general.buttons.backToDashboard', 'Back to Dashboard')}
              </Button>
            )}
          </ToolbarStyled>
        </Container>
      </AppBarStyled>

      <ContainerStyled maxWidth="md">
        <MainStyled component="main">{children}</MainStyled>
      </ContainerStyled>
    </Box>
  );
};
